import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { Modal } from 'flowbite';

import ExpenseDepartmentRow from './expense/ExpenseDepartmentRow';
import RevenueDepartmentRow from './revenue/RevenueDepartmentRow';
import SectionHeader from './shared/SectionHeader';
import SponsorDepartmentRow from './sponsor/SponsorDepartmentRow';
import TicketingRow from './ticketing/TicketingRow';

import { modalOptions } from 'Helpers/modalOptions';

import LoadingOverlay from 'CommonComponents/LoadingOverlay';
import SimpleConfirmationModal from 'CommonComponents/SimpleConfirmationModal';

export default function CashflowCentralModuleTable(props) {
    const [maxMilestonesCount, setMaxMilestonesCount] = useState([]);
    const [milestonePeriods, setMilestonePeriods] = useState([]);
    const [editingExpenseId, setEditingExpenseId] = useState(null);
    const [editingRevenueId, setEditingRevenueId] = useState(null);
    const [editingSponsorId, setEditingSponsorId] = useState(null);
    const [editingType, setEditingType] = useState(null);
    const [editingMilestoneId, setEditingMilestoneId] = useState(null);
    const [editingMilestoneType, setEditingMilestoneType] = useState(null);
    const [modalTitle, setModalTitle] = useState("Milestone couldn't be updated");
    const [modalText, setModalText] = useState('Error updating Milestone values');
    const [cashflowCentralModuleAlertModalElement, setCashflowCentralModuleAlertModalElement] = useState(null);

    const [expenditures, setExpenditures] = useState([]);
    const [sponsorship, setSponsorship] = useState([]);
    const [ticketing, setTicketing] = useState([]);
    const [revenueDepartments, setRevenueDepartments] = useState([]);
    const [showRevenues, setShowRevenues] = useState(true);
    const [showTicketing, setShowTicketing] = useState(true);
    const [showExpenditures, setShowExpenditures] = useState(true);
    const [loading, setLoading] = useState(true);
    const [firstLoading, setFirstLoading] = useState(true);

    const cashflowCentralModuleAlertModalRef = useRef();

    useEffect(() => {
        cashflowCentralModuleAlertModalRef &&
            setCashflowCentralModuleAlertModalElement(new Modal(cashflowCentralModuleAlertModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (props.refreshCashflowCount > 0) {
            fetchCentralCashflow(props.refreshCashflowCount === 1);
        }
    }, [props.refreshCashflowCount]);

    const fetchCentralCashflow = (isFirstTimeLoading = false) => {
        setLoading(true);
        if (isFirstTimeLoading) {
            setFirstLoading(true);
        }
        axios
            .get(props.CashflowCentralModuleIndex, {
                params: {
                    selectedMonths: props.selectedMonths,
                    displayBudgetLines: props.displayBudgetLines,
                },
            })
            .then((res) => {
                setMilestonePeriods(res.data.periods);
                setMaxMilestonesCount(res.data.maxMilestonesCount);
                setExpenditures(res.data.expenditureData.Expenditure);
                setSponsorship(res.data.revenueData.Revenue[0].Sponsorship);
                setTicketing(res.data.revenueData.Revenue[1].Ticketing);
                setRevenueDepartments(res.data.revenueData.Revenue[2].Departments);
                setLoading(false);
                if (isFirstTimeLoading) {
                    setFirstLoading(false);
                }
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
                if (isFirstTimeLoading) {
                    setFirstLoading(false);
                }
            });
    };

    const increaseMaxMilestonesCount = useCallback(() => {
        setMaxMilestonesCount((prev) => prev + 1);
    }, []);

    const setExpenseEditing = useCallback((expense_id, milestone_id, type) => {
        setEditingExpenseId(expense_id);
        setEditingType(type);
        setEditingMilestoneId(milestone_id);
    }, []);

    const setRevenueEditing = useCallback((revenue_id, milestone_id, type, milestone_type = null) => {
        setEditingRevenueId(revenue_id);
        setEditingType(type);
        setEditingMilestoneId(milestone_id);
        setEditingMilestoneType(milestone_type);
    }, []);

    const setSponsorEditing = useCallback((sponsor_id, milestone_id, type, milestone_type = null) => {
        setEditingSponsorId(sponsor_id);
        setEditingType(type);
        setEditingMilestoneId(milestone_id);
        setEditingMilestoneType(milestone_type);
    }, []);

    const modalShow = useCallback(
        (showModal, title = '', text = '') => {
            setModalTitle(title);
            setModalText(text);
            if (showModal) {
                cashflowCentralModuleAlertModalElement && cashflowCentralModuleAlertModalElement.show();
            } else {
                cashflowCentralModuleAlertModalElement && cashflowCentralModuleAlertModalElement.hide();
            }
        },
        [cashflowCentralModuleAlertModalElement],
    );

    // Handle expand all trigger from parent with staggered updates
    useEffect(() => {
        const t = props.expandAllTrigger;
        if (!t?.version) return;

        // Only update sections when it's the sections batch or no batch specified (fallback)
        if (!t.batch || t.batch === 'sections') {
            setShowRevenues(t.expanded);
            setShowExpenditures(t.expanded);
            setShowTicketing(t.expanded);
        }
    }, [props.expandAllTrigger?.version]);

    // Toggle functions optimized with useCallback
    const toggleRevenues = useCallback(() => setShowRevenues(!showRevenues), [showRevenues]);
    const toggleExpenditures = useCallback(() => setShowExpenditures(!showExpenditures), [showExpenditures]);
    const toggleTicketing = useCallback(() => setShowTicketing(!showTicketing), [showTicketing]);

    // Memoized common props to prevent unnecessary re-renders
    const commonProps = useMemo(
        () => ({
            minDate: props.minDate,
            maxDate: props.maxDate,
            modalShow,
            fetchCentralCashflow,
            canEdit: props.canEdit,
            periods: milestonePeriods,
            expandAllTrigger: props.expandAllTrigger,
            editAllTrigger: props.editAllTrigger,
        }),
        [props.minDate, props.maxDate, props.canEdit, milestonePeriods, modalShow, props.expandAllTrigger, props.editAllTrigger],
    );

    const expenseProps = useMemo(
        () => ({
            ...commonProps,
            ExpenseMilestoneUpdateUrl: props.ExpenseMilestoneUpdateUrl,
            maxExpenseMilestonesCount: Math.max(maxMilestonesCount, milestonePeriods.length),
            increaseMaxExpenseMilestonesCount: increaseMaxMilestonesCount,
            editingExpenseId,
            editingType,
            editingMilestoneId,
            setExpenseEditing: setExpenseEditing,
            setLoading: setLoading,
            costcentersColor: true,
            milestoneType: 'expenditure',
            indentLevel: 0,
        }),
        [
            commonProps,
            props.ExpenseMilestoneUpdateUrl,
            maxMilestonesCount,
            milestonePeriods.length,
            increaseMaxMilestonesCount,
            editingExpenseId,
            editingType,
            editingMilestoneId,
            setExpenseEditing,
        ],
    );

    const revenueProps = useMemo(
        () => ({
            ...commonProps,
            RevenueMilestoneUpdateUrl: props.RevenueMilestoneUpdateUrl,
            maxRevenueMilestonesCount: Math.max(maxMilestonesCount, milestonePeriods.length),
            increaseMaxMilestonesCount: increaseMaxMilestonesCount,
            editingRevenueId,
            editingType,
            editingMilestoneId,
            editingMilestoneType,
            setRevenueEditing,
            setLoading: setLoading,
            costcentersColor: false,
            milestoneType: 'revenue',
            indentLevel: 0,
        }),
        [
            commonProps,
            props.RevenueMilestoneUpdateUrl,
            maxMilestonesCount,
            milestonePeriods.length,
            increaseMaxMilestonesCount,
            editingRevenueId,
            editingType,
            editingMilestoneId,
            editingMilestoneType,
            setRevenueEditing,
        ],
    );

    const sponsorProps = useMemo(
        () => ({
            ...commonProps,
            SponsorMilestoneUpdateUrl: props.SponsorMilestoneUpdateUrl,
            maxSponsorMilestonesCount: Math.max(maxMilestonesCount, milestonePeriods.length),
            increaseMaxMilestonesCount: increaseMaxMilestonesCount,
            editingSponsorId,
            editingType,
            editingMilestoneId,
            editingMilestoneType,
            setSponsorEditing,
            setLoading: setLoading,
            costcentersColor: false,
            milestoneType: 'sponsor',
            indentLevel: 0,
        }),
        [
            commonProps,
            props.SponsorMilestoneUpdateUrl,
            maxMilestonesCount,
            milestonePeriods.length,
            increaseMaxMilestonesCount,
            editingSponsorId,
            editingType,
            editingMilestoneId,
            editingMilestoneType,
            setSponsorEditing,
        ],
    );

    // Memoize data arrays to prevent unnecessary re-renders of child components
    const memoizedExpenditures = useMemo(() => expenditures, [expenditures]);
    const memoizedSponsorship = useMemo(() => sponsorship, [sponsorship]);
    const memoizedTicketing = useMemo(() => ticketing, [ticketing]);
    const memoizedRevenueDepartments = useMemo(() => revenueDepartments, [revenueDepartments]);

    return (
        <>
            <LoadingOverlay isVisible={loading} />
            <style>
                {`
                .inbud-table_first-two-sticky-80 tbody tr > td:nth-child(1) {
                    position: sticky !important;
                    left: 0px !important;
                    z-index: 15 !important;
                    background-color: inherit !important;
                }
                .inbud-table_first-two-sticky-80 tbody tr > td:nth-child(2) {
                    position: sticky !important;
                    left: 20rem !important;
                    z-index: 15 !important;
                    background-color: inherit !important;
                }
                `}
            </style>
            <div className="inbud-table_wrapper tw-max-h-[80vh] tw-overflow-y-auto">
                <table className="inbud-table inbud-table_first-two-sticky-80">
                    <thead className="tw-bg-blue-25">
                        <tr className="*:tw-font-extrabold *:!tw-pb-1.5">
                            <th style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: 'rgb(193 215 253)' }}></th>
                            <th style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: 'rgb(193 215 253)' }}></th>
                            <th className="tw-border-r-2" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}></th>
                            {milestonePeriods.map((milestonePeriod, i) => (
                                <th
                                    key={`th_milestones_${i}`}
                                    colSpan={2}
                                    className="tw-uppercase tw-border-r-2"
                                    style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}
                                >
                                    {milestonePeriod}
                                </th>
                            ))}
                            <th colSpan={2} className="tw-border-r-2" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}>
                                {$t('Total')}
                            </th>
                            <th style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}>{$t('Actions')}</th>
                        </tr>
                        <tr className="*:!tw-pt-1.5">
                            <th className="tw-min-w-80" style={{ position: 'sticky', top: 32, zIndex: 20, backgroundColor: 'rgb(193 215 253)' }}>
                                {$t('Department')} / {$t('Cost Centre')} / {$t('Budget Line')}
                            </th>
                            <th className="tw-min-w-20" style={{ position: 'sticky', top: 32, zIndex: 20, backgroundColor: 'rgb(193 215 253)' }}>
                                {$t('Type')}
                            </th>
                            <th className="tw-min-w-28 tw-border-r-2" style={{ position: 'sticky', top: 32, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}>
                                {$t('Value')}
                            </th>
                            {milestonePeriods.map((milestonePeriod, i) => (
                                <React.Fragment key={`th_milestones_${i}_date_percentage`}>
                                    <th className="tw-min-w-16" style={{ position: 'sticky', top: 32, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}>
                                        %
                                    </th>
                                    <th
                                        className="tw-min-w-32 tw-border-r-2"
                                        style={{ position: 'sticky', top: 32, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}
                                    >
                                        {$t('Value')}
                                    </th>
                                </React.Fragment>
                            ))}
                            <th className="tw-min-w-16" style={{ position: 'sticky', top: 32, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}>
                                %
                            </th>
                            <th className="tw-min-w-32 tw-border-r-2" style={{ position: 'sticky', top: 32, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}>
                                {$t('Value')}
                            </th>
                            <th className="tw-min-w-20" style={{ position: 'sticky', top: 32, zIndex: 10, backgroundColor: 'rgb(193 215 253)' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && firstLoading ? (
                            <tr>
                                <td colSpan="12">{$t('Loading')}...</td>
                            </tr>
                        ) : (
                            <>
                                <SectionHeader
                                    title={$t('Revenue')}
                                    isExpanded={showRevenues}
                                    onToggle={toggleRevenues}
                                    maxMilestonesCount={maxMilestonesCount}
                                />

                                {showRevenues &&
                                    memoizedTicketing &&
                                    memoizedTicketing.value !== null &&
                                    memoizedTicketing.value !== undefined &&
                                    typeof memoizedTicketing === 'object' && (
                                        <TicketingRow
                                            minDate={props.minDate}
                                            maxDate={props.maxDate}
                                            key={`tr_ticketing_`}
                                            revenue={memoizedTicketing}
                                            maxRevenueMilestonesCount={maxMilestonesCount}
                                            periods={milestonePeriods}
                                            indentLevel={0}
                                            displayBudgetLines={props.displayBudgetLines}
                                            expandAllTrigger={props.expandAllTrigger}
                                        />
                                    )}
                                {showRevenues && (!memoizedTicketing || memoizedTicketing.value === null || memoizedTicketing.value === undefined) && (
                                    <tr>
                                        <td colSpan={maxMilestonesCount * 2 + 5}>
                                            {$t('No {{type}} Found!', {
                                                type: 'Ticketing',
                                            })}
                                        </td>
                                    </tr>
                                )}

                                {showRevenues && memoizedSponsorship && memoizedSponsorship.costcenters.length > 0 && (
                                    <SponsorDepartmentRow {...sponsorProps} key={`tr_sponsorship_${memoizedSponsorship.id}`} department={memoizedSponsorship} />
                                )}

                                {showRevenues && memoizedSponsorship && memoizedSponsorship.costcenters.length === 0 && (
                                    <tr>
                                        <td colSpan={maxMilestonesCount * 2 + 5}>
                                            {$t('No {{type}} Found!', {
                                                type: 'Sponsorships',
                                            })}
                                        </td>
                                    </tr>
                                )}

                                {showRevenues &&
                                    memoizedRevenueDepartments.length > 0 &&
                                    memoizedRevenueDepartments.map((department) => (
                                        <RevenueDepartmentRow {...revenueProps} key={`tr_revenue_department_${department.id}`} department={department} />
                                    ))}
                                {showRevenues && memoizedRevenueDepartments.length === 0 && (
                                    <tr>
                                        <td colSpan={maxMilestonesCount * 2 + 5}>
                                            {$t('No {{type}} Found!', {
                                                type: 'Revenue Departments',
                                            })}
                                        </td>
                                    </tr>
                                )}

                                <SectionHeader
                                    title={$t('Expenditure')}
                                    isExpanded={showExpenditures}
                                    onToggle={toggleExpenditures}
                                    maxMilestonesCount={maxMilestonesCount}
                                />
                                {showExpenditures &&
                                    memoizedExpenditures.length > 0 &&
                                    memoizedExpenditures.map((department) => (
                                        <ExpenseDepartmentRow
                                            {...expenseProps}
                                            key={`tr_department_${department.id}`}
                                            department={department}
                                            fetchCentralCashflow={fetchCentralCashflow}
                                        />
                                    ))}
                                {showExpenditures && memoizedExpenditures.length === 0 && (
                                    <tr>
                                        <td colSpan={maxMilestonesCount * 2 + 5}>
                                            {$t('No {{type}} Found!', {
                                                type: 'Expenditure Departments',
                                            })}
                                        </td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
                <SimpleConfirmationModal
                    closeHandler={() => modalShow(false)}
                    allowAction={true}
                    modalId={'cashflow-central-module-alert'}
                    modalRef={cashflowCentralModuleAlertModalRef}
                    modalElement={cashflowCentralModuleAlertModalElement}
                    confirmationMsg={modalText}
                    title={modalTitle}
                    showSubmitButton={false}
                    cancelButtonText={$t('Close')}
                />
            </div>
        </>
    );
}
